import React from 'react';
import { Row, Col, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaInfoCircle } from 'react-icons/fa';

const TOOLTIP_PERCENTUAL =
  'Percentual calculado sobre o total movimentado (inclui títulos em aberto). Diferença para 100% = percentual ainda não liquidado.';

const sacadoCorClass = (cor) => {
  if (cor === '2') return 'bg-danger text-white';
  if (cor === '3') return 'bg-dark text-white';
  return 'bg-success text-white';
};

const brl = (v) =>
  (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const pct = (v) => `${((v || 0) * 100).toFixed(2).replace('.', ',')}%`;

const MetricCard = ({ title, total, reg, extra }) => (
  <div className="card h-100">
    <div className="card-body p-2">
      <div className="text-muted small mb-1">{title}</div>
      <div className="font-weight-bold">{brl(total)}</div>
      {reg !== undefined && (
        <div className="text-muted small">{reg} reg.</div>
      )}
      {extra}
    </div>
  </div>
);

const PercentualLabel = ({ label, value }) => (
  <div className="d-flex align-items-center justify-content-between small mt-1">
    <span>
      {label}{' '}
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip>{TOOLTIP_PERCENTUAL}</Tooltip>}
      >
        <FaInfoCircle className="text-muted" style={{ cursor: 'help' }} />
      </OverlayTrigger>
    </span>
    <strong>{pct(value)}</strong>
  </div>
);

const DashboardSection = ({ data, onAbrirCedentes }) => {
  if (!data) return null;

  const {
    sacado_cor,
    qtd_apresentados,
    prorrogados,
    total_movimentado,
    liquidado_cedente,
    liquidado_banco,
    em_aberto_vencido,
    em_aberto_a_vencer,
    devolvido_liquidado,
    devolvido_aberto,
    soma_geral_liquidado,
    percentual_liquidado_cedente_sobre_movimentado,
    percentual_risco_sobre_movimentado,
    percentual_liquidado_banco_sobre_movimentado,
    percentual_devolvido_sobre_liquidado,
    percentual_dev_aberto_sobre_liquidado,
    media_liquidado_cedente,
    media_risco_cedente,
    media_liquidado_banco,
    limite_excedido,
  } = data;

  return (
    <div className="mb-3">
      {/* Status panel + badge */}
      <div className="d-flex align-items-center mb-2 flex-wrap">
        <span
          className={`badge px-3 py-2 mr-2 ${sacadoCorClass(sacado_cor)}`}
          style={{ fontSize: '0.9rem' }}
        >
          {sacado_cor === '2' ? 'Negativado' : sacado_cor === '3' ? 'Bloqueado' : 'Regular'}
        </span>
        <span className="text-muted small mr-3">
          Qtd. Apresentados: <strong>{qtd_apresentados || 0}</strong>
        </span>
        <span className="text-muted small mr-3">
          Prorrogados: <strong>{prorrogados || 0}</strong>
        </span>
        {limite_excedido && (
          <Badge
            variant="warning"
            className="ml-auto"
            style={{ cursor: 'pointer', fontSize: '0.85rem' }}
            onClick={onAbrirCedentes}
          >
            ⚠ Limite excedido
          </Badge>
        )}
      </div>

      {/* Cards de métricas */}
      <Row className="g-2">
        <Col xs={12} sm={6} md={4} lg={3} className="mb-2">
          <MetricCard
            title="Total Movimentado"
            total={total_movimentado && total_movimentado.total}
            reg={total_movimentado && total_movimentado.reg}
          />
        </Col>
        <Col xs={12} sm={6} md={4} lg={3} className="mb-2">
          <MetricCard
            title="Liquidado pelo Cedente"
            total={liquidado_cedente && liquidado_cedente.total}
            reg={liquidado_cedente && liquidado_cedente.reg}
            extra={
              <div className="small text-muted mt-1">
                <div>Com risco: {brl(liquidado_cedente && liquidado_cedente.total_risco)} ({liquidado_cedente && liquidado_cedente.reg_risco} reg.)</div>
                <div>Média: {brl(media_liquidado_cedente)}</div>
                <div>Média risco: {brl(media_risco_cedente)}</div>
                <PercentualLabel label="% s/ Movimentado" value={percentual_liquidado_cedente_sobre_movimentado} />
                <PercentualLabel label="% Risco s/ Movimentado" value={percentual_risco_sobre_movimentado} />
              </div>
            }
          />
        </Col>
        <Col xs={12} sm={6} md={4} lg={3} className="mb-2">
          <MetricCard
            title="Liquidado pelo Banco"
            total={liquidado_banco && liquidado_banco.total}
            reg={liquidado_banco && liquidado_banco.reg}
            extra={
              <div className="small text-muted mt-1">
                <div>Média: {brl(media_liquidado_banco)}</div>
                <PercentualLabel label="% s/ Movimentado" value={percentual_liquidado_banco_sobre_movimentado} />
              </div>
            }
          />
        </Col>
        <Col xs={12} sm={6} md={4} lg={3} className="mb-2">
          <MetricCard
            title="Soma Geral Liquidado"
            total={soma_geral_liquidado}
          />
        </Col>
        <Col xs={12} sm={6} md={4} lg={3} className="mb-2">
          <MetricCard
            title="Em Aberto — Vencido"
            total={em_aberto_vencido && em_aberto_vencido.total}
            reg={em_aberto_vencido && em_aberto_vencido.reg}
          />
        </Col>
        <Col xs={12} sm={6} md={4} lg={3} className="mb-2">
          <MetricCard
            title="Em Aberto — A Vencer"
            total={em_aberto_a_vencer && em_aberto_a_vencer.total}
            reg={em_aberto_a_vencer && em_aberto_a_vencer.reg}
          />
        </Col>
        <Col xs={12} sm={6} md={4} lg={3} className="mb-2">
          <MetricCard
            title="Devolvido Liquidado"
            total={devolvido_liquidado && devolvido_liquidado.total}
            reg={devolvido_liquidado && devolvido_liquidado.reg}
            extra={
              <PercentualLabel label="% s/ Liquidado" value={percentual_devolvido_sobre_liquidado} />
            }
          />
        </Col>
        <Col xs={12} sm={6} md={4} lg={3} className="mb-2">
          <MetricCard
            title="Devolvido Aberto"
            total={devolvido_aberto && devolvido_aberto.total}
            reg={devolvido_aberto && devolvido_aberto.reg}
            extra={
              <PercentualLabel label="% s/ Liquidado" value={percentual_dev_aberto_sobre_liquidado} />
            }
          />
        </Col>
      </Row>
    </div>
  );
};

export default DashboardSection;
